import { Breadcrumb, Button, Progress } from "antd";
import {
  Link,
  useLocation,
  useNavigate,
  useOutletContext,
  useParams,
} from "react-router-dom";
import { formatDate, handleRouteNav, numberFormatter } from "../utils";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CustomBadge from "../components/Charts/CustomBadge";
import {
  PiShoppingCartFill,
  PiUserCircle,
  PiUserCircleFill,
} from "react-icons/pi";
import { useApiCall } from "../services/api";
import PrimaryButton from "../components/Forms/PrimaryButton";
import DataTable from "../components/Tables/Datatable";
import { MdOutlineEdit } from "react-icons/md";
import Block from "../components/models/Block";
import AssignClints from "../components/models/AssignClints";
import AddSchemeModal from "../components/models/AddSchemeModal";
import CancelOrderModal from "../components/models/CancelOrderModal";
import { useGlobalModal } from "../context/SuccessModalProvider";
import { notificationRefresh } from "../redux/auth/cartRefreshSlice";

const AdminSchemeDetails = () => {
  const { user_id, role_id, is_sales_person, full_name } = useSelector(
    (state) => state.auth,
  );
  const { setTitle, setBackButton } = useOutletContext();
  const [schemeData, setSchemeData] = useState();
  const { postApi, loading } = useApiCall();
  const location = useLocation();
  const { mode, offerId, offerRequestId } = location.state || {}; // mode = true means it is offer details, otherwise request details
  const scheme_id = offerId;
  const [isActionModal, setIsActionModal] = useState(false);
  const [actionType, setActionType] = useState("");
  const navigate = useNavigate();
  const [assignClientModal, setAssignClientModal] = useState(false);
  const [userType, setUserType] = useState("customer");
  const [selectedClientData, setSelectedClientData] = useState([]);
  const [clients, setClients] = useState([]);
  const [allUsers, setAllUsers] = useState([]);
  const [searchUser, setSearchUser] = useState("");
  const [isSchemeModalVisible, setSchemeModalVisible] = useState(false);
  const [requestedClientsBy, setRequestedClientsBy] = useState();
  const [assginedClients, setAssginedClients] = useState([]);
  const [cancelRemark, setCancelRemark] = useState(null);
  const [assignUsers, setAssignUser] = useState([]);
  const { showPopup } = useGlobalModal();
  const [fetchRefresh, setFetchRefresh] = useState(false);
  const dispatch = useDispatch();
  const fetchIdRef = useRef(0);

  const assignedClientsColumns = [
    {
      title: "ID",
      dataIndex: "customer_id",
      width: 80,
    },
    {
      title: "Company Name",
      render: (recored) => <span>{recored?.company_name}</span>,
      ellipsis: true,
    },
    {
      title: "Current Qty",
      render: (record) => {
        const val = record?.current_qty;
        const type = schemeData?.qty_type;

        if (val == null || val === "") return "--";

        let num = Number(val);
        if (isNaN(num)) return val;
        if (num % 1 !== 0) {
          num = num.toFixed(2);
        }

        num = numberFormatter(num);

        return `${num} ${type === "Meter" ? "Mtrs" : type || "Cases"}`;
      },
      width: 150,
    },
    {
      title: "Target Qty",
      dataIndex: "target_qty",
      render: (val) => {
        const type = schemeData?.qty_type;

        if (val == null || val === "") return "--";

        let num = Number(val);
        if (isNaN(num)) return val;
        if (num % 1 !== 0) {
          num = num.toFixed(2);
        }

        num = numberFormatter(num);

        return `${num} ${type === "Meter" ? "Mtrs" : type || "Cases"}`;
      },
      width: 150,
    },
    {
      title: "Incentive",
      dataIndex: "credit_value",
      width: 150,
    },
    {
      title: "Assigned Sales",
      render: (record) => <span>{record?.saleperson?.full_name}</span>,
      width: 200,
      ellipsis: true,
    },
  ];

  if (!mode) {
    const lastItem = assignedClientsColumns.pop();
    assignedClientsColumns.splice(2, 0, lastItem);
  }

  const requestClientsColumn = [
    {
      title: "#",
      dataIndex: "customer_id",
      width: 80,
    },
    {
      title: "Company Name",
      dataIndex: "company_name",
      ellipsis: true,
    },
  ];

  const handleCancel = () => {
    setIsActionModal(false);
  };

  const handleActionOk = async () => {
    const res = await postApi(
      `${actionType === "approve" ? "sales/offer/offer-request/approved" : "sales/offer/offer-request/rejected"}`,
      {
        offer_request_id: requestedClientsBy?.offer_request_id,
        remarks: cancelRemark?.trim(),
      },
      true,
    );
    if (res && res.status) {
      dispatch(notificationRefresh());
      setIsActionModal(false);
      navigate(handleRouteNav("/scheme-requests", role_id));
    }
  };

  useEffect(() => {
    setTitle("Scheme Details");
    setBackButton(true);
  }, [setTitle, setBackButton]);

  const fetchOfferDetails = async () => {
    const payload = {
      offer_id: scheme_id,
      user_id: is_sales_person ? user_id : undefined,
    };
    const res = await postApi("sales/offer/details", payload);
    if (res && res.data) {
      setSchemeData(res.data);

      if (res?.data?.users?.length > 0) {
        if (is_sales_person) {
          const assignUserFilter = res?.data?.users.filter(
            (items) => items?.user_id == user_id,
          );
          setAssignUser(assignUserFilter || []);
        } else {
          setAssignUser(res.data.users || []);
        }
      }

      const currentRequest = offerRequestId
        ? res.data?.offer_request_customers?.find(
            (c) => c.offer_request_id === offerRequestId,
          )
        : res.data?.offer_request_customers?.find(
            (c) => c.status === "Pending",
          );

      if (
        offerRequestId &&
        currentRequest?.offer_request_id !== offerRequestId
      ) {
        return showPopup({
          title: null,
          content: <span className="custom-modal-title">Data not found!</span>,
          onOk: () => {
            navigate(-1);
          },
          className: "custom-success-modal",
        });
      }

      setRequestedClientsBy(currentRequest);

      if (res.data?.customers?.length > 0) {
        const currentUserId = currentRequest?.updated_by_id;

        const sortedClients = [...res.data.customers].sort((a, b) => {
          if (!mode) {
            const aIsMine = a?.saleperson?.user_id === currentUserId;
            const bIsMine = b?.saleperson?.user_id === currentUserId;
            if (aIsMine && !bIsMine) return -1;
            if (!aIsMine && bIsMine) return 1;
          }
          return (a?.saleperson?.full_name || "").localeCompare(
            b?.saleperson?.full_name || "",
          );
        });

        setAssginedClients(sortedClients);
      } else {
        setAssginedClients([]);
      }
    } else {
      showPopup({
        title: null,
        content: <span className="custom-modal-title">{res.message}</span>,
        onOk: () => {
          navigate(-1);
        },
        className: "custom-success-modal",
      });
    }
  };

  useEffect(() => {
    fetchOfferDetails();
  }, [location?.state?.refresh, fetchRefresh]);

  const fetchUsers = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      const body = {
        page: 1,
        per_page: 10000,
        search: searchUser,
      };
      const res = await postApi("user/list", body);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      if (!res?.status) {
        setAllUsers([]);
        return;
      }
      if (res && res.data) {
        setAllUsers(res.data);
      }
    } catch (err) {}
  }, [searchUser]);

  const fetchClients = useCallback(async () => {
    fetchIdRef.current += 1;
    const currentFetchId = fetchIdRef.current;
    try {
      const body = {
        page: 0,
        per_page: 0,
        search: searchUser,
        user_id: is_sales_person ? user_id : undefined,
      };
      const res = await postApi("sales/assign-customer-list", body);
      if (currentFetchId !== fetchIdRef.current) {
        return;
      }
      if (!res?.status) {
        setClients([]);
        return;
      }
      if (res && res.data) {
        setClients(res.data);
      }
    } catch (err) {}
  }, [is_sales_person, searchUser, user_id]);

  const onSearch = (value) => {
    setSearchUser(value);
  };

  useEffect(() => {
    if (userType === "customer") {
      fetchClients();
    } else if (userType === "salesperson") {
      fetchUsers();
    }
  }, [fetchClients, fetchUsers, userType]);

  const getSelectedClientData = (data, type) => {
    const ids = data.map((item) =>
      type === "customer" ? Number(item.customer_id) : Number(item.user_id),
    );

    setSelectedClientData(ids);
  };

  const getSelectedClients = async (data) => {
    const payloadForClient = {
      offer_id: schemeData?.offer_id,
      customer_ids: data,
    };

    const payloadForClientRequest = {
      offer_id: schemeData?.offer_id,
      offer_request_customer_ids: data,
      updated_by: full_name,
      updated_by_id: user_id,
    };

    const res = await postApi(
      `${is_sales_person ? "sales/offer/offer-request" : "sales/offer/assign-clients"}`,
      is_sales_person ? payloadForClientRequest : payloadForClient,
      true,
    );
    if (res && res.status) {
      setAssignClientModal(false);
      fetchOfferDetails();
    }
  };

  const updateClints = () => {
    setAssignClientModal(true);
    getSelectedClientData(
      requestedClientsBy?.status === "Pending" && is_sales_person
        ? requestedClientsBy?.customers || []
        : schemeData?.customers || [],
      "customer",
    );
    setUserType("customer");
  };

  if (!offerId) {
    return navigate(handleRouteNav("/discount-schemes", role_id));
  }

  return (
    <>
      <>
        <div className="pb-7 mt-6">
          <div
            className="bg-[#FFFFFF] leading-normal p-6"
            style={{ borderRadius: "32px" }}
          >
            <div className="flex justify-between items-center ">
              <div className="flex gap-4 items-center">
                <div
                  className="bg-[#F4F4F4] p-2 w-fit shrink-0"
                  style={{ borderRadius: "24px" }}
                >
                  <img
                    className="min-h-[64px] min-w-[64px] "
                    src="/assets/icons/welcome-logo.png"
                  ></img>
                </div>

                <div className="text-left">
                  <strong className="text-xl text-[#000]">
                    {schemeData?.title}
                  </strong>
                  <div className="text-[#00000080] mt-2">
                    {formatDate(schemeData?.from_date)} -{" "}
                    {formatDate(schemeData?.to_date)}
                  </div>
                </div>
              </div>

              <div>
                <CustomBadge
                  variant={
                    schemeData?.status === "Active"
                      ? "success"
                      : schemeData?.status === "Inactive"
                        ? "pending"
                        : "danger"
                  }
                  text={schemeData?.status}
                  divClassName={"me-2"}
                ></CustomBadge>
              </div>
            </div>
          </div>

          <div
            className="bg-[#FFFFFF] leading-normal p-6 mt-7"
            style={{ borderRadius: "32px" }}
          >
            <div className="text-left flex justify-between items-center">
              <strong className="text-xl text-[#000]">
                Scheme Information
              </strong>
              {mode && !is_sales_person && (
                <PrimaryButton
                  text="Edit"
                  icon={<MdOutlineEdit fontSize={"20px"} />}
                  iconOnleft
                  onClick={() => setSchemeModalVisible(true)}
                />
              )}
            </div>
            <div className="">
              {schemeData?.variants?.map((item, index) => (
                <>
                  <div>
                    <div className="text-left mt-6">
                      <strong className="text-xl text-[#000]">
                        Slab {index + 1}
                      </strong>
                      <div className="grid grid-cols-2 md:grid-cols-2 gap-6  mt-4">
                        <div className="flex gap-3 items-center">
                          <img src="/assets/icons/quantity.svg"></img>
                          <div className="">
                            <div className="text-[#00000080] ">Quantity</div>
                            <strong className="text-lg text-[#000] mt-2">
                              {numberFormatter(item?.variant_qty)}{" "}
                              {schemeData?.qty_type === "Meter"
                                ? "Mtrs"
                                : schemeData?.qty_type || "Cases"}
                            </strong>
                          </div>
                        </div>

                        <div className="flex gap-3 items-center">
                          <img src="/assets/icons/incentive.svg"></img>
                          <div className="">
                            <div className="text-[#00000080] ">Incentive</div>
                            <strong className="text-lg text-[#000] mt-2 capitalize">
                              {item?.variant_incentive}{" "}
                              {schemeData?.credit_type == "percentage"
                                ? "%"
                                : "Rs. / Mtr"}
                            </strong>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 ">
            <div
              className="bg-[#FFFFFF] leading-normal mt-7 text-black p-6"
              style={{ borderRadius: "32px" }}
            >
              <div className="flex justify-between items-center">
                <div className="text-left">
                  <strong className="text-xl text-[#000]">Products</strong>
                </div>
                <div>
                  <Button
                    type="primary"
                    className="h-[45px] bg-[#7070701A] hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-sm sm:text-base whitespace-nowrap !cursor-default"
                    size="middle"
                  >
                    {schemeData?.product_details?.length || 0} products
                  </Button>
                </div>
              </div>

              <div className="overflow-auto h-[400px]">
                {schemeData?.product_details?.map((item) => (
                  <>
                    <div
                      className="flex justify-between items-center gap-3 mt-6 pb-6"
                      style={{ borderBottom: "1px solid #DDDDDD" }}
                    >
                      <div>
                        <div className="flex justify-between items-center gap-3">
                          <img
                            className="h-[88px] w-[88px]"
                            style={{ borderRadius: "16px" }}
                            src={item?.product?.product_pic_url}
                          ></img>

                          <div>
                            <div className="text-left">
                              <strong className="text-xl text-[#000]">
                                {item?.short_name}
                              </strong>
                              <div className="text-[#979797] flex items-center gap-2 mt-2">
                                {item?.clienttype} | {item?.fabric_type}
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </>
                ))}
              </div>
            </div>

            <div
              className="bg-[#FFFFFF] leading-normal mt-7 text-black p-6"
              style={{ borderRadius: "32px", overflow: "hidden" }}
            >
              <div className="flex justify-between items-center">
                <div className="text-left">
                  <strong className="text-xl text-[#000]">
                    Assigned Sales
                  </strong>
                </div>
                <div>
                  <Button
                    type="primary"
                    className="h-[45px] bg-[#7070701A] hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-sm sm:text-base whitespace-nowrap !cursor-default"
                    size="middle"
                  >
                    {assignUsers?.length} salespersons
                  </Button>
                </div>
              </div>

              <div className="overflow-auto h-[400px]">
                {assignUsers?.length > 0 &&
                  assignUsers.map((item, i) => (
                    <>
                      <div
                        className="flex justify-between items-center gap-3 mt-6 pb-6"
                        style={{ borderBottom: "1px solid #DDDDDD" }}
                      >
                        <div>
                          <div className="flex items-center gap-10">
                            <div className="text-left">
                              <span className="text-xl text-[#000]">
                                {item?.user_id}
                              </span>
                            </div>

                            <div>
                              <div className="text-left px-5">
                                <span className="text-xl text-[#000]">
                                  {item?.full_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  ))}
                {assignUsers?.length === 0 && (
                  <div className="flex flex-col items-center gap-3 mt-10">
                    <img
                      src="/assets/icons/empty-removebg-preview.png"
                      className="h-[150px]"
                    />
                    <div className="text-[#00000080]">
                      No salespersons assigned
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div
            className={
              !mode ||
              (is_sales_person && requestedClientsBy?.status === "Pending")
                ? "grid grid-cols-1 md:grid-cols-2 gap-6 "
                : ""
            }
          >
            <div className="bg-white rounded-[32px] mt-6 pb-6">
              <div className="flex justify-between items-center gap-3 px-8 py-6">
                <div>
                  <strong className="text-xl text-[#000]">
                    Assigned Clients
                  </strong>
                </div>
                <div className="flex items-center gap-3">
                  {((!is_sales_person && mode) ||
                    (is_sales_person &&
                      mode &&
                      requestedClientsBy?.status !== "Pending")) && (
                    <PrimaryButton
                      text="Update Clients"
                      noIcon
                      onClick={updateClints}
                    />
                  )}

                  <Button
                    type="primary"
                    className="h-[45px] bg-[#7070701A] hover:!bg-[#7070701A] hover:!text-[#A33434] font-bold text-[#A33434] text-sm sm:text-base whitespace-nowrap !cursor-default"
                    size="middle"
                  >
                    {assginedClients?.length || 0} clients
                  </Button>
                </div>
              </div>
              {assginedClients?.length > 0 && (
                <div className="mb-6">
                  <DataTable
                    columns={assignedClientsColumns}
                    data={assginedClients}
                    Showpagination={false}
                    scroll={{ y: 400 }}
                    disableHover
                  />
                </div>
              )}

              {assginedClients?.length === 0 && (
                <div className="flex flex-col items-center gap-3 mt-10 ">
                  <img
                    src="/assets/icons/empty-removebg-preview.png"
                    className="h-[150px]"
                  />
                  <div className="text-[#00000080] mb-20">
                    No clients assigned
                  </div>
                </div>
              )}
            </div>
            {(!mode ||
              (is_sales_person &&
                requestedClientsBy?.status === "Pending")) && (
              <div className="bg-white rounded-[32px] mt-6 relative">
                <div className="flex justify-between items-start gap-3 px-8 py-6">
                  <div className="text-left">
                    <strong className="text-xl text-[#000]">
                      {requestedClientsBy?.customers?.length || 0} Requested
                      Clients
                    </strong>
                    <div className="text-[#00000080]">
                      By - {requestedClientsBy?.updated_by}
                    </div>
                  </div>
                  <div className="flex items-center gap-3">
                    {mode &&
                    is_sales_person &&
                    requestedClientsBy?.status === "Pending" ? (
                      <PrimaryButton
                        text="Update Clients"
                        noIcon
                        onClick={updateClints}
                      />
                    ) : (
                      <CustomBadge
                        variant={
                          requestedClientsBy?.status === "Approved"
                            ? "success"
                            : requestedClientsBy?.status === "Pending"
                              ? "pending"
                              : "danger"
                        }
                        text={requestedClientsBy?.status}
                        divClassName={"absolute top-3 right-5"}
                      ></CustomBadge>
                    )}
                  </div>
                </div>
                {requestedClientsBy?.customers?.length > 0 && (
                  <DataTable
                    columns={requestClientsColumn}
                    data={requestedClientsBy?.customers}
                    Showpagination={false}
                    showHeader={false}
                    className="mb-6"
                    scroll={{ y: 430 }}
                  />
                )}

                {requestedClientsBy?.customers?.length === 0 && (
                  <div className="flex flex-col items-center gap-3 mt-10 mb-20">
                    <img
                      src="/assets/icons/empty-removebg-preview.png"
                      className="h-[150px]"
                    />
                    <div className="text-[#00000080]">No clients requested</div>
                  </div>
                )}
              </div>
            )}
          </div>

          {schemeData?.remarks && schemeData?.remarks?.trim() && (
            <div
              className="bg-[#FFFFFF] leading-normal p-6 mt-7"
              style={{ borderRadius: "32px" }}
            >
              <div className="text-left flex gap-3 items-center">
                <div className="w-[50px] h-[50px] bg-[#A334341A] rounded-full flex items-center justify-center">
                  <PiUserCircle size={24} fill="#A33434" />
                </div>
                <div>
                  <strong className="text-xl text-[#000]">
                    Special Remarks
                  </strong>
                </div>
              </div>

              <div className="space-y-4 mt-8 ps-4 text-[#000] text-start">
                <span className="flex whitespace-pre-line">
                  {schemeData?.remarks?.replace(/\\n/g, "\n")}
                </span>
              </div>
            </div>
          )}
          {!mode &&
            !is_sales_person &&
            requestedClientsBy?.status === "Pending" && (
              <div className="text-center flex justify-center gap-5 mt-6">
                <PrimaryButton
                  btnType="secondary"
                  noIcon
                  onClick={() => {
                    setActionType("approve");
                    setIsActionModal(true);
                  }}
                >
                  Approve
                </PrimaryButton>
                <PrimaryButton
                  btnType="secondary"
                  noIcon
                  onClick={() => {
                    setActionType("reject");
                    setIsActionModal(true);
                  }}
                >
                  Reject
                </PrimaryButton>
              </div>
            )}
        </div>
      </>

      <Block
        open={isActionModal && actionType == "approve"}
        onCancel={handleCancel}
        onConfirm={handleActionOk}
        title={`Do you want to ${actionType} this request?`}
        confirmText={
          actionType === "approve" ? "Approve Request" : "Reject Request"
        }
        loading={loading}
      />

      <AssignClints
        title={`${userType === "customer" ? "Assign Clients" : "Assign Salespersons"}`}
        open={assignClientModal}
        handleSubmit={getSelectedClients}
        onClose={() => setAssignClientModal(false)}
        selectedUserData={selectedClientData}
        userData={userType === "customer" ? clients : allUsers}
        onSearch={onSearch}
        userType={userType}
        loading={loading}
      />

      <AddSchemeModal
        isOpen={isSchemeModalVisible}
        closeModal={() => setSchemeModalVisible(false)}
        schemeData={schemeData}
        isEdit={true}
        title="Edit Scheme"
        btnText="Update Scheme"
        handleSubmit={() => {
          setFetchRefresh((prev) => !prev);
        }}
      ></AddSchemeModal>
      <CancelOrderModal
        open={actionType == "reject" && isActionModal}
        onClose={() => setIsActionModal(false)}
        handleSubmit={handleActionOk}
        setCancelRemark={setCancelRemark}
        cancelRemark={cancelRemark}
        loading={loading}
        title="Reject Request"
        btntext="Reject Request"
      ></CancelOrderModal>
    </>
  );
};

export default AdminSchemeDetails;
